import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useUserRole } from '@/hooks/useUserRole';
import type { SyncTargetStatus, SyncConfig, AdminUser, AdminRole } from '@/api/admin';
import type { StoreListItem } from '@/api/customers';
import '@/styles/screens.css';
import '@/styles/admin.css';

type Tab = 'sync' | 'users';

/** Admin section (admin-only): Sync + Users & roles. */
export function AdminPage() {
  const { isAdmin, loading } = useUserRole();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('sync');

  // Guard: only admins. (Nav already hides it, but defend the route directly too.)
  if (!loading && !isAdmin) {
    return (
      <div className="screen">
        <p className="state-msg">You don&rsquo;t have access to the Admin panel.</p>
        <div style={{ textAlign: 'center' }}>
          <button className="btn btn--ghost" onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin">
      <aside className="admin-side">
        <p className="admin-side-title">Admin</p>
        <button
          className={`admin-side-item${tab === 'sync' ? ' is-active' : ''}`}
          onClick={() => setTab('sync')}
        >
          Sync
        </button>
        <button
          className={`admin-side-item${tab === 'users' ? ' is-active' : ''}`}
          onClick={() => setTab('users')}
        >
          Users &amp; roles
        </button>
      </aside>
      <div className="admin-main">
        {tab === 'sync' && <SyncTab />}
        {tab === 'users' && <UsersTab />}
      </div>
    </div>
  );
}

// ── Sync tab ─────────────────────────────────────────────────────────────────

function SyncTab() {
  const api = useApi();
  const [targets, setTargets] = useState<SyncTargetStatus[] | null>(null);
  const [config, setConfig] = useState<SyncConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.syncStatus(), api.getSyncConfig()])
      .then(([t, c]) => { setTargets(t); setConfig(c); })
      .catch(() => setError('Could not load sync settings.'));
  }, [api]);

  if (error) return <div className="screen"><p className="state-msg state-msg--error">{error}</p></div>;
  if (!targets || !config) {
    return <div className="screen"><div className="skeleton-row" style={{ height: 60, marginBottom: 12 }} /><div className="skeleton-row" style={{ height: 120 }} /></div>;
  }

  // Group targets by source system, rendered in a stable, friendly order.
  const SOURCES: { key: string; label: string }[] = [
    { key: 'bc', label: 'Business Central' },
    { key: 'shopify', label: 'Shopify' },
    { key: 'klaviyo', label: 'Klaviyo' },
  ];
  const known = new Set(SOURCES.map((s) => s.key));
  const groups = [
    ...SOURCES,
    // Any unexpected source still shows, under its raw key.
    ...[...new Set(targets.map((t) => t.source))].filter((s) => !known.has(s)).map((s) => ({ key: s, label: s })),
  ].filter((g) => targets.some((t) => t.source === g.key));

  return (
    <div className="admin-content">
      <h1 className="screen-title">Sync</h1>

      <CronSettings config={config} onSave={(cron, enabled) => api.setSyncConfig(cron, enabled).then(setConfig)} />

      {groups.map((g) => (
        <section className="admin-block" key={g.key}>
          <p className="admin-block-title">{g.label}</p>
          <div className="sync-grid">
            {targets.filter((t) => t.source === g.key).map((t) => (
              <SyncCard key={t.target} row={t} onSync={() => api.runSync(t.target).then(setTargets)} />
            ))}
          </div>
        </section>
      ))}

      <p className="admin-note">Klaviyo sources will appear here as they&rsquo;re connected.</p>
    </div>
  );
}

function CronSettings({ config, onSave }: { config: SyncConfig; onSave: (cron: string, enabled: boolean) => Promise<void> }) {
  const [cron, setCron] = useState(config.cronExpression);
  const [enabled, setEnabled] = useState(config.enabled);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = cron !== config.cronExpression || enabled !== config.enabled;

  async function save() {
    setSaving(true); setSaved(false);
    try { await onSave(cron.trim(), enabled); setSaved(true); } finally { setSaving(false); }
  }

  return (
    <section className="admin-block">
      <p className="admin-block-title">Schedule</p>
      <div className="cron-row">
        <label className="cron-toggle">
          <input type="checkbox" checked={enabled} onChange={(e) => { setEnabled(e.target.checked); setSaved(false); }} />
          <span>Automatic nightly sync</span>
        </label>
        <div className="cron-expr">
          <span className="cron-label">Schedule (cron)</span>
          <input value={cron} onChange={(e) => { setCron(e.target.value); setSaved(false); }} spellCheck={false} />
        </div>
        <button className="btn btn--primary btn--sm" disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && <span className="cron-saved">Saved</span>}
      </div>
      <p className="admin-note">
        When on, all sources sync automatically on this schedule. (Manual sync below always works.)
      </p>
    </section>
  );
}

function SyncCard({ row, onSync }: { row: SyncTargetStatus; onSync: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    setBusy(true); setErr(null);
    try { await onSync(); } catch (e) { setErr((e as Error).message || 'Sync failed'); } finally { setBusy(false); }
  }

  const last = row.last_synced_at ? new Date(row.last_synced_at).toLocaleString('en-IE') : 'never';
  const statusClass = row.last_status === 'ok' ? 'sync-ok' : row.last_status === 'error' ? 'sync-err' : 'sync-none';

  return (
    <div className="sync-card">
      <div className="sync-card-head">
        <span className="sync-card-title">{row.label}</span>
        <span className={`sync-dot ${statusClass}`} title={row.last_status ?? 'not run'} />
      </div>
      <div className="sync-card-count">{row.record_count ?? '—'}<span className="sync-card-unit">records</span></div>
      <dl className="sync-meta">
        <div><dt>Source</dt><dd>{row.endpoint ?? '—'}</dd></div>
        <div><dt>Endpoint</dt><dd className="sync-url">/api/guest-admin/sync/{row.target}</dd></div>
        <div><dt>Last synced</dt><dd>{last}</dd></div>
      </dl>
      {(err || row.last_error) && <p className="sync-error">{err || row.last_error}</p>}
      <button className="btn btn--primary btn--sm sync-btn" disabled={busy} onClick={run}>
        {busy ? 'Syncing…' : 'Sync now'}
      </button>
    </div>
  );
}

// ── Users & roles tab ────────────────────────────────────────────────────────

const ROLE_LABELS: { key: AdminRole; label: string; hint: string }[] = [
  { key: 'admin', label: 'Admin', hint: 'everything + this panel' },
  { key: 'office', label: 'Office', hint: 'all stores, no admin panel' },
  { key: 'store', label: 'Store', hint: 'assigned store(s) only' },
];

function UsersTab() {
  const api = useApi();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [stores, setStores] = useState<StoreListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null); // app_user_id with store editor open
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.listUsers().then(setUsers).catch(() => setError('Couldn’t load users.'));
    api.stores().then(setStores).catch(() => {});
  }, [api]);

  async function changeRole(u: AdminUser, role: AdminRole) {
    try {
      setUsers(await api.updateUser(u.app_user_id, { role }));
    } catch { setError('Couldn’t update role.'); }
  }

  async function changeStores(u: AdminUser, codes: string[]) {
    try {
      setUsers(await api.updateUser(u.app_user_id, { storeCodes: codes }));
    } catch { setError('Couldn’t update stores.'); }
  }

  if (error && !users) return <p className="state-msg state-msg--error">{error}</p>;
  if (!users) {
    return <div>{Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton-row" style={{ marginBottom: 8 }} />)}</div>;
  }

  return (
    <div className="admin-content">
      <div className="section-head">
        <h2 className="admin-title">Users &amp; roles</h2>
        <button className="btn btn--sm" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add user'}
        </button>
      </div>
      <p className="admin-note" style={{ marginTop: 0 }}>
        Accounts live in Entra ID — this page assigns app access. A user added here gets their
        role and store(s) the moment they first sign in. Store logins are shared per-shop
        accounts; managers&rsquo; personal logins are linked to the same store.
      </p>
      {error && <p className="state-msg state-msg--error">{error}</p>}

      {adding && (
        <AddUserForm
          stores={stores}
          onCreated={(list) => { setUsers(list); setAdding(false); }}
          create={(input) => api.createUser(input)}
          onError={setError}
        />
      )}

      <div className="user-list">
        {users.filter((u) => !u.deactivated_at).map((u) => (
          <div key={u.app_user_id} className="user-row">
            <div className="user-id">
              <span className="user-name">
                {u.display_name || u.email || '—'}
                {u.pending && <span className="row-badge row-badge--note">Invited</span>}
              </span>
              <span className="user-meta">
                {u.email}
                {u.last_login
                  ? ` · last seen ${new Date(u.last_login).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}`
                  : ' · never signed in'}
              </span>
            </div>

            <select
              className="toolbar-select"
              aria-label={`Role for ${u.email}`}
              value={u.role}
              onChange={(e) => changeRole(u, e.target.value as AdminRole)}
            >
              {ROLE_LABELS.map((r) => <option key={r.key} value={r.key}>{r.label} — {r.hint}</option>)}
            </select>

            <label className="user-can-email" title="Allow this user to send outreach email as themselves">
              <input
                type="checkbox"
                checked={u.can_email}
                onChange={(e) => {
                  api.updateUser(u.app_user_id, { canEmail: e.target.checked })
                    .then(setUsers)
                    .catch(() => setError('Couldn’t update the email permission.'));
                }}
              />
              Email
            </label>

            <div className="user-stores">
              {u.role === 'store' ? (
                <>
                  <span className="user-store-codes">
                    {u.store_codes.length ? u.store_codes.join(', ') : 'No store assigned'}
                  </span>
                  <button className="btn btn--ghost btn--sm" onClick={() => setEditing(editing === u.app_user_id ? null : u.app_user_id)}>
                    {editing === u.app_user_id ? 'Done' : 'Edit stores'}
                  </button>
                </>
              ) : (
                <span className="user-store-codes">All stores</span>
              )}
              <button
                className="btn btn--ghost btn--sm"
                title={u.pending
                  ? 'Remove this invite'
                  : 'Remove: blocks sign-in and strips access. Their name stays on all notes and history. Reversible.'}
                onClick={() => {
                  const msg = u.pending
                    ? `Remove the invite for ${u.email}?`
                    : `Remove ${u.display_name || u.email}? They can no longer sign in; their name stays on everything they wrote. You can reactivate later.`;
                  if (window.confirm(msg)) {
                    api.deleteUser(u.app_user_id).then(setUsers).catch(() => setError('Couldn’t remove the user.'));
                  }
                }}
              >
                Remove
              </button>
            </div>

            {editing === u.app_user_id && u.role === 'store' && (
              <StorePicker
                stores={stores}
                selected={u.store_codes}
                onToggle={(code, on) =>
                  changeStores(u, on ? [...u.store_codes, code] : u.store_codes.filter((c) => c !== code))
                }
              />
            )}
          </div>
        ))}
      </div>

      {users.some((u) => u.deactivated_at) && (
        <>
          <p className="admin-block-title" style={{ marginTop: 24 }}>Removed</p>
          <p className="admin-note" style={{ marginTop: 0 }}>
            Removed users can&rsquo;t sign in, but their names stay on everything they wrote.
          </p>
          <div className="user-list">
            {users.filter((u) => u.deactivated_at).map((u) => (
              <div key={u.app_user_id} className="user-row user-row--removed">
                <div className="user-id">
                  <span className="user-name">{u.display_name || u.email || '—'}</span>
                  <span className="user-meta">
                    {u.email} · removed {new Date(u.deactivated_at!).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <span className="user-store-codes">No access</span>
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    if (window.confirm(`Reactivate ${u.display_name || u.email}? You'll need to re-assign their store(s).`)) {
                      api.reactivateUser(u.app_user_id).then(setUsers).catch(() => setError('Couldn’t reactivate the user.'));
                    }
                  }}
                >
                  Reactivate
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StorePicker({
  stores, selected, onToggle,
}: {
  stores: StoreListItem[];
  selected: string[];
  onToggle: (code: string, on: boolean) => void;
}) {
  return (
    <div className="store-picker">
      {stores.map((s) => {
        const on = selected.includes(s.code);
        return (
          <label key={s.store_id} className={`store-pick${on ? ' is-on' : ''}`}>
            <input type="checkbox" checked={on} onChange={(e) => onToggle(s.code, e.target.checked)} />
            {s.name} ({s.code})
          </label>
        );
      })}
    </div>
  );
}

function AddUserForm({
  stores, create, onCreated, onError,
}: {
  stores: StoreListItem[];
  create: (input: { email: string; displayName?: string; role: AdminRole; storeCodes: string[] }) => Promise<AdminUser[]>;
  onCreated: (users: AdminUser[]) => void;
  onError: (msg: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<AdminRole>('store');
  const [codes, setCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const valid = email.includes('@') && (role !== 'store' || codes.length > 0);

  async function submit() {
    setBusy(true);
    try {
      onCreated(await create({ email: email.trim(), displayName: displayName.trim() || undefined, role, storeCodes: role === 'store' ? codes : [] }));
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      onError(msg ?? 'Couldn’t create the user.');
    } finally { setBusy(false); }
  }

  return (
    <div className="add-user-form">
      <div className="add-user-fields">
        <input placeholder="email@obrienswine.ie" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input placeholder="Display name (optional)" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        <select value={role} onChange={(e) => setRole(e.target.value as AdminRole)} aria-label="Role">
          {ROLE_LABELS.map((r) => <option key={r.key} value={r.key}>{r.label} — {r.hint}</option>)}
        </select>
      </div>
      {role === 'store' && (
        <StorePicker
          stores={stores}
          selected={codes}
          onToggle={(code, on) => setCodes((c) => (on ? [...c, code] : c.filter((x) => x !== code)))}
        />
      )}
      <div className="detail-actions">
        <button className="btn btn--sm" disabled={busy || !valid} onClick={submit}>Create user</button>
      </div>
    </div>
  );
}
